/**
 * Manual receipt (`receipt_orders`) registries + wire contracts
 * (epic #1031 E2-R1, #1059 — sales system phase 2).
 *
 * Receipts are the entry path for off-system money: large collections
 * over bank / alipay / wechat / usdt that never touch ZPay
 * `payment_orders`. Lifecycle is maker-checker:
 *
 *   submitted → credited | rejected | voided
 *
 * Sales submit (entered_by, session-stamped via ops-api); finance
 * verifies + credits or rejects/voids. The crediting batch (E2-R3
 * #1061) is a topup-equivalent atomic money path: balance tx type
 * 'external_topup' + revenue-ledger 'topup' row + cash-classified
 * credit lot + balance bump + receipt flip, all in one gated batch.
 *
 * Every status change writes its transition row in the same SQL batch
 * as the conditional status flip (same discipline as the E1 approval
 * engine); `ops_audit` is a best-effort supplement, never the source
 * of truth for state history.
 */

import type { BrandId } from './brands.ts';

// ─── registries ───────────────────────────────────────────────────────────

export const RECEIPT_ORDER_STATUSES = [
  'submitted',
  'credited',
  'rejected',
  'voided',
] as const;
export type ReceiptOrderStatus = (typeof RECEIPT_ORDER_STATUSES)[number];

export function isReceiptOrderStatus(value: unknown): value is ReceiptOrderStatus {
  return (
    typeof value === 'string' &&
    (RECEIPT_ORDER_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * Accepted receipt currencies (Roy, 2026-07-08: RMB + USD). The column
 * stays TEXT so USDT etc. can extend later without a migration — this
 * registry is the API-layer source of truth.
 */
export const RECEIPT_CURRENCIES = ['CNY', 'USD'] as const;
export type ReceiptCurrency = (typeof RECEIPT_CURRENCIES)[number];

export function isReceiptCurrency(value: unknown): value is ReceiptCurrency {
  return (
    typeof value === 'string' && (RECEIPT_CURRENCIES as readonly string[]).includes(value)
  );
}

export const RECEIPT_CHANNELS = ['bank', 'alipay', 'wechat', 'usdt', 'other'] as const;
export type ReceiptChannel = (typeof RECEIPT_CHANNELS)[number];

export function isReceiptChannel(value: unknown): value is ReceiptChannel {
  return (
    typeof value === 'string' && (RECEIPT_CHANNELS as readonly string[]).includes(value)
  );
}

/**
 * Invoice lifecycle on a receipt (epic #1031 E2-R6, #1064):
 *
 *   none              — the customer does not want an invoice (the
 *                       receipt was submitted with need_invoice false).
 *   open → issued     — finance issued the invoice. Only valid while
 *                       the RECEIPT is `credited` — an invoice must
 *                       never be issued against money that was never
 *                       credited (409 `RECEIPT_ORDER_NOT_CREDITED`
 *                       otherwise).
 *   open → voided     — finance decided no invoice will be issued
 *                       (also requires a `credited` receipt), OR the
 *                       receipt itself was rejected/voided, which
 *                       closes a still-open invoice to `voided` in the
 *                       SAME batch so no dead receipt lingers in the
 *                       finance invoicing worklist.
 *
 * Coupling invariant: reject/void only fire from `submitted` (before
 * an invoice can be issued) and auto-close open invoices, while issue
 * requires `credited` (terminal) — so an ISSUED invoice can never
 * coexist with a rejected/voided receipt.
 *
 * The `open → issued | voided` flip is FINANCE-ONLY and stamped
 * (`issued_by_ops_user_id` + `issued_at` record the deciding actor for
 * both terminal states — the receipt reject/void actor when the close
 * was automatic) + audited via `ops_audit`. Sales only declare need +
 * tax point at submit time.
 */
export const RECEIPT_INVOICE_STATUSES = ['none', 'open', 'issued', 'voided'] as const;
export type ReceiptInvoiceStatus = (typeof RECEIPT_INVOICE_STATUSES)[number];

export function isReceiptInvoiceStatus(value: unknown): value is ReceiptInvoiceStatus {
  return (
    typeof value === 'string' &&
    (RECEIPT_INVOICE_STATUSES as readonly string[]).includes(value)
  );
}

/**
 * Per-brand invoicing mode (epic #1031 AC7):
 *
 *   cn-tax     — CN VAT invoice (发票) with a tax point (票点) in basis
 *                points. The tax point is an INVOICING COST that reduces
 *                the company's net on the receipt — it NEVER reduces the
 *                customer's credited amount (same discipline as
 *                `actual_fee_minor`, which is the distinct CHANNEL fee).
 *   commercial — commercial invoice (nxtpath); no CN tax point exists,
 *                so a submitted `tax_point_bps` is rejected outright.
 */
export const BRAND_INVOICE_MODES = ['cn-tax', 'commercial'] as const;
export type BrandInvoiceMode = (typeof BRAND_INVOICE_MODES)[number];

export function isBrandInvoiceMode(value: unknown): value is BrandInvoiceMode {
  return (
    typeof value === 'string' && (BRAND_INVOICE_MODES as readonly string[]).includes(value)
  );
}

/** Hard ceiling for any tax point: 10 000 bps = 100%. */
export const MAX_TAX_POINT_BPS = 10_000;

// ─── records ──────────────────────────────────────────────────────────────

/**
 * One row of `receipt_orders`. Money fields are integer minor units in
 * the receipt currency (CNY cents / USD cents) — never floats.
 *
 * Credit-time fields (`verified_by_ops_user_id`, `owner_ops_user_id`,
 * `applied_rate`, `credited_usd_micros`, `cny_gross_cents`,
 * `cny_net_cents`, `credited_at`, `balance_tx_id`) are NULL until the
 * E2-R3 crediting batch populates them atomically at verification.
 * `owner_ops_user_id` is the verification-time owner stamp (the Epic 5
 * commission feed). `statement_id` is reserved Epic 4 settlement
 * linkage with no FK semantics yet.
 */
export interface ReceiptOrderRecord {
  id: string;
  org_id: string;
  account_id: string;
  /** Stamped server-side at submit from `accounts.signup_brand`; immutable. */
  brand: BrandId;
  amount_minor: number;
  currency: ReceiptCurrency;
  channel: ReceiptChannel;
  /** Absolute fee in receipt currency minor units (no formula). */
  actual_fee_minor: number;
  received_at: string;
  contract_ref: string | null;
  entered_by_ops_user_id: string;
  status: ReceiptOrderStatus;
  submitted_at: string;
  /** Submit idempotency key (UNIQUE). */
  client_ref: string;
  verified_by_ops_user_id: string | null;
  owner_ops_user_id: string | null;
  applied_rate: number | null;
  credited_usd_micros: number | null;
  cny_gross_cents: number | null;
  cny_net_cents: number | null;
  credited_at: string | null;
  balance_tx_id: string | null;
  statement_id: string | null;
  /** Sales-confirmed at submit: does the customer want an invoice? */
  need_invoice: boolean;
  /**
   * Brand invoicing mode resolved from `brand_invoice_settings` at
   * submit time; `null` when `need_invoice` is false.
   */
  invoice_mode: BrandInvoiceMode | null;
  /**
   * Tax point (票点) in basis points — cn-tax invoices only, `null`
   * otherwise. Recorded faithfully for the Epic 5 commission base
   * (verified receipts net of tax point and actual fees); this record
   * never applies it to any amount.
   */
  tax_point_bps: number | null;
  invoice_title: string | null;
  tax_no: string | null;
  invoice_status: ReceiptInvoiceStatus;
  /** Finance actor of the terminal `issued`/`voided` invoice decision. */
  issued_by_ops_user_id: string | null;
  issued_at: string | null;
  created_at: string;
  updated_at: string;
  /**
   * CRM-maintained customer company name (`customer_contacts.company`),
   * resolved at READ time — not a `receipt_orders` column. Live value:
   * a later CRM edit changes what historical receipts display. `null`
   * when the org has no contact card or the company field is empty.
   */
  customer_company: string | null;
}

/**
 * One row of `receipt_order_transitions` — the synchronous, in-batch
 * state history. `from_status` is `null` exactly once per receipt: the
 * creation (submit) event, written in the same batch as the receipt
 * INSERT.
 */
export interface ReceiptOrderTransitionRecord {
  id: string;
  receipt_id: string;
  from_status: ReceiptOrderStatus | null;
  to_status: ReceiptOrderStatus;
  actor_ops_user_id: string;
  note: string | null;
  created_at: string;
}

// ─── /internal/receipts contracts ─────────────────────────────────────────

/**
 * `POST /internal/receipts`. Brand is NOT accepted on the wire —
 * platform-api stamps it from `accounts.signup_brand` (the brand
 * authority). `entered_by_ops_user_id` is the sales actor, derived from
 * the ops session by ops-api; nothing in this table ever trusts a
 * browser-supplied identity.
 *
 * Idempotency: a repeat submit with an existing `client_ref` returns
 * the existing receipt (`created: false`) and writes nothing.
 */
export interface SubmitReceiptOrderRequest {
  client_ref: string;
  org_id: string;
  account_id: string;
  /** Integer minor units in `currency` (CNY cents / USD cents), > 0. */
  amount_minor: number;
  /** Validated against {@link RECEIPT_CURRENCIES} at the API layer. */
  currency: string;
  /** Validated against {@link RECEIPT_CHANNELS} at the API layer. */
  channel: string;
  /** Integer minor units, >= 0. Defaults to 0 when omitted. */
  actual_fee_minor?: number;
  received_at: string;
  contract_ref?: string | null;
  entered_by_ops_user_id: string;
  /** Optional note recorded on the creation transition row. */
  note?: string | null;
  /**
   * Invoice need + tax point, sales-confirmed at submit (epic #1031
   * E2-R6, #1064). Validated against the brand's
   * `brand_invoice_settings` row: a cn-tax brand accepts (or defaults) a
   * tax point in bps within the configured cap; a commercial brand
   * rejects any tax point and records commercial mode. Invoice fields
   * without `need_invoice: true` are rejected.
   */
  need_invoice?: boolean;
  /** Basis points; cn-tax brands only. Omitted → the brand default. */
  tax_point_bps?: number | null;
  invoice_title?: string | null;
  tax_no?: string | null;
}

export interface SubmitReceiptOrderResponse {
  receipt: ReceiptOrderRecord;
  /** false when `client_ref` matched an existing receipt (idempotent replay). */
  created: boolean;
}

/**
 * `GET /internal/receipts` filters: `status`, `brand`, `org_id`,
 * `entered_by_ops_user_id`, `invoice_status`, `limit`, `offset`.
 */
export interface ListReceiptOrdersResponse {
  receipts: ReceiptOrderRecord[];
  total: number;
  limit: number;
  offset: number;
}

/** `GET /internal/receipts/:id` — receipt plus its full transition history. */
export interface GetReceiptOrderResponse {
  receipt: ReceiptOrderRecord;
  transitions: ReceiptOrderTransitionRecord[];
}

/**
 * `POST /internal/receipts/:id/reject`. Transitions only from
 * `submitted` through a conditional `UPDATE … WHERE status='submitted'`
 * gate: exactly one concurrent decision wins; the loser receives
 * `RECEIPT_ORDER_NOT_SUBMITTED` (409) with zero partial writes. A
 * still-open invoice (`invoice_status='open'`) is closed to `voided`
 * in the SAME statement (stamping the rejecting actor as the invoice
 * decider) — a rejected receipt never lingers in the invoicing
 * worklist.
 */
export interface RejectReceiptOrderRequest {
  actor_ops_user_id: string;
  note?: string | null;
}

export interface RejectReceiptOrderResponse {
  receipt: ReceiptOrderRecord;
  transition: ReceiptOrderTransitionRecord;
}

/** `POST /internal/receipts/:id/void` — same conditional gate (and same in-batch invoice close) as reject. */
export interface VoidReceiptOrderRequest {
  actor_ops_user_id: string;
  note?: string | null;
}

export interface VoidReceiptOrderResponse {
  receipt: ReceiptOrderRecord;
  transition: ReceiptOrderTransitionRecord;
}

/**
 * `POST /internal/receipts/:id/credit` — finance verification +
 * crediting as ONE action (epic #1031 assumption: no separate
 * "verified but not credited" state).
 *
 * Implemented in E2-R3 (#1061): a topup-equivalent atomic batch —
 * conditional flip first (the row-locking `status='submitted'` gate),
 * then balance tx / revenue-ledger row / credit lot / balance bump all
 * correlated on the flipped state, plus the transition row. Concurrent
 * decisions collapse to one winner; losers get
 * `RECEIPT_ORDER_NOT_SUBMITTED` (409) with zero partial writes. The
 * request carries NO amounts: credited always equals verified at the
 * `exchange_rate_policy` rate (CNY) or 1:1 (USD, no fabricated L1).
 *
 * `actor_ops_user_id` is the finance verifier (becomes
 * `verified_by_ops_user_id`); the FX snapshot and the owner stamp are
 * computed server-side at credit time (policy rate via
 * `exchange_rate_policy`, owner = the customer's active manager), never
 * accepted from the caller.
 */
export interface CreditReceiptOrderRequest {
  actor_ops_user_id: string;
  note?: string | null;
}

export interface CreditReceiptOrderResponse {
  receipt: ReceiptOrderRecord;
  transition: ReceiptOrderTransitionRecord;
  /**
   * USD micro-amount of open AR this credit settled against the customer's
   * billing statements (epic #1033 E4-C6, #1130). FIFO oldest-open first; 0
   * for non-credit customers and for credit customers with no open AR (the
   * whole credit stays as a prepaid balance in that case). Always ≤ the
   * credited USD amount.
   */
  settled_ar_usd_micros: number;
}

// ─── invoice actions (epic #1031 E2-R6, #1064) ────────────────────────────

/**
 * `POST /internal/receipts/:id/invoice/issue` — finance issues the
 * invoice on a CREDITED receipt whose `invoice_status` is `open`. The
 * flip is a conditional
 * `UPDATE … WHERE invoice_status='open' AND status='credited'` gate
 * stamping `issued_by_ops_user_id` + `issued_at`; an uncredited
 * receipt is refused with `RECEIPT_ORDER_NOT_CREDITED` (409, zero
 * writes) and the loser of a concurrent invoice decision receives
 * `RECEIPT_INVOICE_NOT_OPEN` (409). The optional note lands in the
 * `ops_audit` metadata (invoice state history is the stamped columns,
 * not a transition table). `actor_ops_user_id` is the finance actor,
 * session-derived by ops-api.
 */
export interface IssueReceiptInvoiceRequest {
  actor_ops_user_id: string;
  note?: string | null;
}

export interface IssueReceiptInvoiceResponse {
  receipt: ReceiptOrderRecord;
}

/**
 * `POST /internal/receipts/:id/invoice/void` — finance decides no
 * invoice will be issued. Same `open`-only + `credited`-only
 * conditional gate and stamps as issue.
 */
export interface VoidReceiptInvoiceRequest {
  actor_ops_user_id: string;
  note?: string | null;
}

export interface VoidReceiptInvoiceResponse {
  receipt: ReceiptOrderRecord;
}

// ─── brand invoice settings (epic #1031 E2-R6, #1064) ────────────────────

/**
 * One row of `brand_invoice_settings` — per-brand invoice configuration
 * following the DB-config pattern: config lives in an admin-editable
 * table, never env vars.
 *
 * `default_tax_point_bps` pre-fills the submit form and backs an
 * omitted tax point on cn-tax submits; `max_tax_point_bps` is the
 * accepted cap. Commercial brands carry `null` default and `0` max —
 * no tax point is ever accepted for them.
 */
export interface BrandInvoiceSettingsRecord {
  brand: BrandId;
  mode: BrandInvoiceMode;
  default_tax_point_bps: number | null;
  max_tax_point_bps: number;
  updated_at: string | null;
  updated_by_ops_user_id: string | null;
}

/** `GET /internal/brand-invoice-settings` — every registered brand's row. */
export interface ListBrandInvoiceSettingsResponse {
  settings: BrandInvoiceSettingsRecord[];
}

/**
 * `PUT /internal/brand-invoice-settings/:brand` — full-state update
 * (admin-only at the ops-api gate, audited). cn-tax requires
 * `max_tax_point_bps` in (0, {@link MAX_TAX_POINT_BPS}] and a default of
 * `null` or within the cap; commercial forces `null` default and `0`
 * max.
 */
export interface UpdateBrandInvoiceSettingsRequest {
  /** Validated against {@link BRAND_INVOICE_MODES}. */
  mode: string;
  default_tax_point_bps?: number | null;
  max_tax_point_bps?: number;
}

export interface UpdateBrandInvoiceSettingsResponse {
  settings: BrandInvoiceSettingsRecord;
}
